import "./PatientProfileHeader.scss";

import React, { useEffect, useState } from "react";
import {
  CarePlanDto,
  Census,
  GetPatientDetails,
  InternalCareTeamMembers,
  PatientPersonalInfo,
} from "patients-experience-client";
import { formatDate, getAge } from "../../helper/dateFormatter";
import {
  createCarePlanApi,
  createPatientApi,
} from "../../api-helpers/PatientApiHelper";
import { ReactComponent as CensusActive } from "../../assets/img/CensusActive.svg";
import { ReactComponent as CensusInactive } from "../../assets/img/CensusInactive.svg";
import { Link } from "react-router-dom";
import { createClinicalFormsApi } from "../../api-helpers/ClinicalFormsApiHelper";
import { GridDataRequest } from "clinical-forms-experience-client";
import { RootState } from "../../store";
import { useDispatch, useSelector } from "react-redux";
import { setPatientRpmOnly } from "../../store/slices/patientRpmOnly";
import { setLastAssessmentDate } from "../../store/slices/lastAssessmentDate";
import { WorkflowState } from "common-util-experience-client";
import { createWorkflowApi } from "../../api-helpers/CommonUtilsApiHelper";
import { Dropdown, Modal, ModalBody, OverlayTrigger, Tooltip } from "react-bootstrap";
import ViewCarePlan from "../care-plan/ViewCarePlan";
import { formatDateTimeToLocal } from "../schedule/dateFormat";
import Toaster, { ToastConfig } from "../../components/Toaster";
import { ErrorTypes } from "../../interfaces/errors";
import { createGridApi } from "../../api-helpers/GridApiHelper";
import { LoadingHelper } from "../../components/loader/LoadingHelper";
import { Pathway_Lookup } from "../../constants/Lookups";
import OffCanvas from "../../components/offCanvas";
import { AddEditAlert } from "../CommandCenter/AddEditAlert";
import ConfirmationBox from "../../components/ConfirmationBox";
interface PatientProfileHeaderProps {
  patientProfile: PatientPersonalInfo;
}

// stateDesc is the raw, unmapped value from the source (health-portal
// correlation) system. These are the values it's known to send today; an
// unrecognized string or null (not yet correlated) falls back to
// PATIENT_HEADER_STATE_FALLBACK below rather than throwing.
type PatientHeaderState =
  | "active"
  | "deactivated"
  | "bounced"
  | "unreachable"
  | "suspended"
  | "non_compliant";

type PatientHeaderStateMeta = {
  label: string;
  background: string;
  ink: string;
  dividerColor: string;
  glyph: "dot" | "speech" | "crossed";
  dotColor?: string;
};

const PATIENT_HEADER_STATE_META: Record<
  PatientHeaderState,
  PatientHeaderStateMeta
> = {
  active: {
    label: "Active",
    background: "#EAF7F5",
    ink: "#0F5C52",
    dividerColor: "#DCE3EA",
    glyph: "dot",
    dotColor: "#1F8577",
  },
  deactivated: {
    label: "Deactivated",
    background: "#F6F8FA",
    ink: "#2E4052",
    dividerColor: "#DCE3EA",
    glyph: "dot",
    dotColor: "#68798A",
  },
  unreachable: {
    label: "Unreachable",
    background: "#FEF3DF",
    ink: "#8A4B00",
    dividerColor: "#F2D9A8",
    glyph: "crossed",
  },
  // bounced/suspended/non_compliant have no approved design yet - these
  // reuse the closest existing treatment (bounced ~ unreachable's "needs
  // attention" amber; suspended ~ deactivated's neutral grey; non_compliant
  // reuses the old "complaint" speech-bubble treatment) as a placeholder.
  // Flag to design before shipping these three to real patients.
  bounced: {
    label: "Bounced",
    background: "#FEF3DF",
    ink: "#8A4B00",
    dividerColor: "#F2D9A8",
    glyph: "crossed",
  },
  suspended: {
    label: "Suspended",
    background: "#F6F8FA",
    ink: "#2E4052",
    dividerColor: "#DCE3EA",
    glyph: "dot",
    dotColor: "#68798A",
  },
  non_compliant: {
    label: "Non-compliant",
    background: "#FEF3DF",
    ink: "#8A4B00",
    dividerColor: "#F2D9A8",
    glyph: "speech",
  },
};

// Used when stateDesc is null (patient not yet correlated to a
// health-portal record) or an unrecognized raw value.
const PATIENT_HEADER_STATE_FALLBACK: PatientHeaderStateMeta = {
  label: "Unknown",
  background: "#F6F8FA",
  ink: "#47596B",
  dividerColor: "#DCE3EA",
  glyph: "dot",
  dotColor: "#93A3B2",
};

const isPatientHeaderState = (
  value: string,
): value is PatientHeaderState =>
  Object.prototype.hasOwnProperty.call(PATIENT_HEADER_STATE_META, value);

const getPatientInitials = (firstName?: string, lastName?: string) => {
  const initials = `${firstName?.trim()?.[0] ?? ""}${lastName?.trim()?.[0] ?? ""}`;
  return initials ? initials.toUpperCase() : "—";
};

// State segment glyphs (speech-bubble for "complaint", crossed-signal for
// "unreachable"/"bounced") share the same icon wrapper, just different path
// data - see PATIENT_HEADER_STATE_META's glyph field.
const STATE_GLYPH_PATHS: Record<"speech" | "crossed", string[]> = {
  speech: ["M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"],
  crossed: [
    "M2 2l20 20",
    "M9.5 4.2A9 9 0 0 1 21 13",
    "M3 13a9 9 0 0 1 3-6.6",
    "M7 17.5a5 5 0 0 1 7.6-1.2",
  ],
};

const StateGlyphIcon = ({
  glyph,
  stroke,
}: {
  glyph: "speech" | "crossed";
  stroke: string;
}) => (
  <svg
    width="15"
    height="15"
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {STATE_GLYPH_PATHS[glyph].map((d) => (
      <path key={d} d={d}></path>
    ))}
  </svg>
);

export const PatientProfileHeader: React.FC<PatientProfileHeaderProps> = ({
  patientProfile,
}) => {
  const [censusFlag, setCensusFlag] = useState<boolean>(false);
  const [rpmOnlyFlag, setRpmOnlyFlag] = useState<boolean>(false);
  const [rpmOnlyConfirmShow, setRpmOnlyConfirmShow] = useState(false);
  const [avatarImgError, setAvatarImgError] = useState(false);
  const [modalShow, setModalShow] = useState(false);
  const [offCanvasShow, setOffCanvasShow] = useState(false);
  const [welcomeMailResendModal, setWelcomeMailResendModal] = useState(false);
  const [patientPathwayProgram, setPatientPathwayProgram] =
    useState<GetPatientDetails>();
  const [internalCareTeam, setInternalCareTeam] =
    useState<InternalCareTeamMembers>();
  const [carePlan, setCarePlan] = useState({} as CarePlanDto);
  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState<ToastConfig>({
    type: "success" as ErrorTypes,
    title: "",
    icon: "",
  });
  const [partnerName, setPartnerName] = useState<string>();
  const [partnerSharePointLink, setPartnerSharePointLink] = useState<
    string | null
  >(null);

  const patientApi = createPatientApi();
  const carePlanApi = createCarePlanApi();
  const workflowApi = createWorkflowApi();
  const clinicalformsApi = createClinicalFormsApi();
  const gridApi = createGridApi();

  const dispatch = useDispatch();

  const pathwayValues = Pathway_Lookup.pathwayValues;

  const pathwayStateId = patientPathwayProgram?.pathway?.pathwayWfStateId || "";

  const patientProfileReloadKey = useSelector(
    (state: RootState) => state.patientProfileReloadKey.patientProfileReloadKey,
  );

  useEffect(() => {
    void getInternalCareTeam();
  }, [patientProfileReloadKey]);

  useEffect(() => {
    setAvatarImgError(false);
  }, [patientProfile?.patientId]);

  useEffect(() => {
    const loadContract = async () => {
      const response = await patientApi.patientsControllerGetPatientContracts(
        patientProfile.patientId,
      );
      if (response.data.length > 0) {
        setPartnerName(response.data[0].partnerName);

        if (response.data[0].sharepointLink) {
          setPartnerSharePointLink(response.data[0].sharepointLink);
        }
      }
    };
    void loadContract();
  }, [patientProfile]);

  const handleUpdateCensus = async () => {
    const requestBody: Census = {
      isCensus: !censusFlag,
    };
    try {
      const censusResponse = await patientApi.patientsControllerUpdateCensus(
        requestBody,
        patientProfile.patientId!,
      );
      setCensusFlag(censusResponse.data?.isCensus);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRpmOnlyToggleClick = () => {
    setRpmOnlyConfirmShow(true);
  };

  const handleConfirmRpmOnlyToggle = async () => {
    const nextRpmOnly = !rpmOnlyFlag;
    LoadingHelper.showLoader(".main");
    try {
      const rpmOnlyResponse = await patientApi.patientsControllerUpdateRpmOnly(
        { rpmOnly: nextRpmOnly },
        patientProfile.patientId!,
      );
      setRpmOnlyFlag(rpmOnlyResponse.data?.rpmOnly);
      dispatch(
        setPatientRpmOnly({
          patientId: patientProfile.patientId!,
          rpmOnly: !!rpmOnlyResponse.data?.rpmOnly,
        })
      );
      handleShowToaster({
        type: "success" as ErrorTypes,
        title: nextRpmOnly
          ? "Patient marked as RPM Only."
          : "RPM Only removed from patient.",
        icon: "check_circle_outline",
      });
    } catch (error) {
      console.error(error);
      handleShowToaster({
        type: "danger" as ErrorTypes,
        title: "Unable to update RPM Only status.",
        icon: "error_outline",
      });
    } finally {
      LoadingHelper.hideLoader("main");
      setRpmOnlyConfirmShow(false);
    }
  };

  const getPatientPathwayProgram = async () => {
    try {
      const patientPathwayProgram =
        await patientApi.patientsControllerGetPatientDetails(
          patientProfile.patientId,
        );
      const pathwayProgramResponse = patientPathwayProgram.data;
      setPatientPathwayProgram(pathwayProgramResponse);
    } catch (error) {
      console.error(error);
    }
  };

  const getPatientAssessments = async () => {
    try {
      const request: GridDataRequest = {
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: patientProfile.patientId,
          },
          {
            propertyName: "status",
            operator: "equals",
            propertyValue: "submitted",
          },
        ],
        sortCriteria: [
          {
            propertyName: "submittedAt",
            descending: true,
          },
        ],
        startRow: 0,
        endRow: 300,
      };
      const patientAssessments = (
        await gridApi.gridControllerGetAssessmentsGridData(request)
      ).data;
      const patientAssessmentsResponse = patientAssessments.data || [];
      getLastAssessmentDate(
        patientAssessmentsResponse.map((item: any) => item.submittedAt),
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getLastAssessmentDate = (dates: string[]) => {
    const lastAssessmentOn = dates.sort((a: string, b: string) => {
      return new Date(b).getTime() - new Date(a).getTime();
    });
    dispatch(setLastAssessmentDate(formatDate(lastAssessmentOn[0])));
  };

  const getInternalCareTeam = async () => {
    try {
      const internalCareTeam =
        await patientApi.patientsControllerGetInternalCareTeam(
          patientProfile.patientId,
        );
      const internalCareTeamResponse = internalCareTeam.data;
      setInternalCareTeam(internalCareTeamResponse);
    } catch (error) {
      console.error(error);
    }
  };

  const getStateId = async (stateName: string): Promise<string[]> => {
    let response,
      carePlanStatuses: WorkflowState[] = [];
    try {
      response =
        await workflowApi.workflowControllerGetStatesForWorkflow("CarePlan");
      carePlanStatuses = response.data.filter(
        (state: WorkflowState) =>
          state.stateName?.toLowerCase() === stateName.toLowerCase(),
      );
    } catch (error) {
      console.log(error);
    }
    return carePlanStatuses.length > 0 ? [carePlanStatuses[0].stateId] : [];
  };

  const loadCarePlanDetails = async (id: string) => {
    try {
      const response =
        await carePlanApi.carePlanControllerGetCarePlanDetails(id);
      setCarePlan(response.data);
    } catch (error) {
      console.log(error);
    }
  };

  const getCarePlan = async () => {
    try {
      const completedId = await getStateId("completed");
      const carePlanPayload = {
        filters: [
          {
            propertyName: "patientId",
            operator: "equals",
            propertyValue: patientProfile.patientId,
          },
          {
            propertyName: "designerStateId",
            operator: "in",
            propertyValue: completedId,
            updateFilter: true,
          },
        ],
        sortCriteria: [{ propertyName: "reviewedAt", descending: true }],
        startRow: 0,
        endRow: 0,
      };
      const response =
        await carePlanApi.carePlanControllerGetGridData(carePlanPayload);
      if (response.data.data.length > 0) {
        const latestCarePlan = response.data?.data?.[0];
        void loadCarePlanDetails(latestCarePlan.id);
        setModalShow(true);
      } else {
        setShowToast(true);
        setToastConfig({
          type: "warning" as ErrorTypes,
          title: "No Completed Care Plan Found.",
          icon: "error_outline",
        });
      }
    } catch (error) {
      console.log(error);
    } finally {
      // setLoading(false);
    }
  };

  const handleViewCarePlan = () => {
    void getCarePlan();
  };

  const handleAddClinicalAlert = () => {
    setOffCanvasShow(true);
  };

  const handleShowToaster = (toasterConfig: ToastConfig) => {
    setToastConfig(toasterConfig as ToastConfig);
    setShowToast(true);
  };

  useEffect(() => {
    setCensusFlag(patientProfile?.isCensus);
    setRpmOnlyFlag(patientProfile?.rpmOnly);
    if (patientProfile?.patientId) {
      dispatch(
        setPatientRpmOnly({
          patientId: patientProfile.patientId,
          rpmOnly: !!patientProfile.rpmOnly,
        })
      );
    }
    void getPatientPathwayProgram();
    void getInternalCareTeam();
    void getPatientAssessments();
  }, []);

  const resendWelcomeMail = async () => {
    LoadingHelper.showLoader(".main");
    try {
      const response =
        await patientApi.patientsControllerGetPatientPersonalInfo(
          patientProfile.patientId,
        );
      await patientApi.patientsControllerResendWelcomeEmail(
        response.data.email,
      );
      toastConfig.title = "Welcome email sent to Patient successfully!";
      toastConfig.type = "success";
    } catch {
      toastConfig.type = "danger";
      toastConfig.title = "Unable to resend patient welcome email.";
    } finally {
      setShowToast(true);
      setWelcomeMailResendModal(false);
      LoadingHelper.hideLoader("main");
    }
  };
  return (
    <>
      <div className="row">
        <Toaster
          show={showToast}
          onClose={() => setShowToast(false)}
          type={toastConfig.type}
          title={toastConfig.title}
          message={toastConfig.message}
          icon={toastConfig.icon}
          addClass={"mt-0 py-1 min-w-22-5"}
        />
      </div>

      <div className="px-4">
        <ol className="breadcrumb ph-breadcrumb pt-2">
          <li className="breadcrumb-item breadcrumb-item-first">
            <Link to="/home">
              <span className="material-symbols-outlined fs-14">home</span>
            </Link>
          </li>
          <li className="breadcrumb-item">
            <Link to="/patients">Patients</Link>
          </li>
          <li className="breadcrumb-item active" aria-current="page">
            Profile
          </li>
        </ol>
      </div>
      {(() => {
          const stateMeta =
            patientProfile?.stateDesc &&
            isPatientHeaderState(patientProfile.stateDesc)
              ? PATIENT_HEADER_STATE_META[patientProfile.stateDesc]
              : PATIENT_HEADER_STATE_FALLBACK;
          const pathwayTooltip =
            patientPathwayProgram?.pathway && pathwayValues[pathwayStateId]
              ? pathwayValues[pathwayStateId]?.title
              : undefined;
          const pathwayLabel = patientPathwayProgram?.pathway?.currentPathway;
          const pathwayPill = (
            <span className="ph-pathway-pill">{pathwayLabel}</span>
          );
          const programPartnerLabel = [
            patientPathwayProgram?.program?.type,
            partnerName,
          ]
            .filter(Boolean)
            .join(" · ");
          const activeTagCount = patientProfile?.activeTagCount ?? 0;

          return (
            <div className="ph">
              <div className="ph-identity">
                <button
                  type="button"
                  className="ph-identity__flag"
                  onClick={handleUpdateCensus}
                  aria-label={
                    censusFlag
                      ? "Mark patient as inactive"
                      : "Mark patient as active"
                  }
                >
                  {censusFlag ? <CensusActive /> : <CensusInactive />}
                </button>
                {!avatarImgError ? (
                  <img
                    className="ph-identity__avatar-img"
                    src="https://cdn-icons-png.flaticon.com/512/168/168719.png"
                    alt=""
                    onError={() => setAvatarImgError(true)}
                  />
                ) : (
                  <span className="ph-identity__avatar" aria-hidden="true">
                    {getPatientInitials(
                      patientProfile?.firstName,
                      patientProfile?.lastName,
                    )}
                  </span>
                )}
                <div className="ph-identity__main">
                  <div className="ph-identity__name-row">
                    <h1 className="ph-identity__name">
                      {patientProfile?.firstName} {patientProfile?.lastName}
                    </h1>
                    {pathwayLabel &&
                      (pathwayTooltip ? (
                        <OverlayTrigger
                          trigger={["hover", "focus"]}
                          placement="top"
                          overlay={<Tooltip>{pathwayTooltip}</Tooltip>}
                        >
                          {pathwayPill}
                        </OverlayTrigger>
                      ) : (
                        pathwayPill
                      ))}
                    {programPartnerLabel && (
                      <span
                        className="ph-identity__subline"
                        title={programPartnerLabel}
                      >
                        {partnerSharePointLink ? (
                          <a
                            href={partnerSharePointLink}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {programPartnerLabel}
                          </a>
                        ) : (
                          programPartnerLabel
                        )}
                      </span>
                    )}
                  </div>
                  <div className="ph-identity__demographics">
                    <span className="ph-identity__demographics-item">
                      <span className="ph-identity__demographics-value">
                        {patientProfile?.dob
                          ? `${formatDate(patientProfile.dob)} (${getAge(patientProfile.dob)})`
                          : "—"}
                      </span>
                    </span>
                    <span className="ph-identity__demographics-divider" />
                    <span className="ph-identity__demographics-item">
                      <span className="ph-identity__demographics-value">
                        {patientProfile?.genderInfo?.type ?? "—"}
                      </span>
                    </span>
                  </div>
                </div>
                <span className="ph-identity__spacer" />
                <button
                  type="button"
                  className="ph-careplan-btn"
                  onClick={handleViewCarePlan}
                >
                  View Care Plan
                  <span className="ph-careplan-btn__arrow" aria-hidden="true">
                    <span className="material-symbols-outlined">
                      arrow_forward
                    </span>
                  </span>
                </button>
                <Dropdown className="ph-overflow" drop="down">
                  <Dropdown.Toggle
                    bsPrefix={" "}
                    id="patient-header-actions-dropdown"
                    variant={" "}
                    className="ph-overflow__btn"
                    aria-label="More patient actions"
                  >
                    <span className="material-symbols-outlined">
                      more_horiz
                    </span>
                  </Dropdown.Toggle>
                  <Dropdown.Menu
                    align="end"
                    renderOnMount
                    popperConfig={{
                      strategy: "fixed",
                      modifiers: [
                        { name: "flip", enabled: false },
                        {
                          name: "preventOverflow",
                          options: { altAxis: false },
                        },
                      ],
                    }}
                  >
                    <button
                      className="dropdown-item d-flex align-items-center"
                      onClick={() => handleAddClinicalAlert()}
                    >
                      <span className="material-symbols-outlined fs-20 align-text-top me-2">
                        notifications
                      </span>
                      Add an escalation
                    </button>
                    <button
                      className="dropdown-item d-flex align-items-center"
                      onClick={() => setWelcomeMailResendModal(true)}
                    >
                      <span className="material-symbols-outlined fs-20 align-text-top me-2">
                        lock_reset
                      </span>
                      Send welcome email
                    </button>
                  </Dropdown.Menu>
                </Dropdown>
              </div>

              <div className="ph-strip">
                <span className="ph-status-group">
                  {patientProfile?.spireHealthPortalId && (
                    <>
                      <a
                        className="ph-status-group__segment ph-status-group__segment--hp"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`https://hpd.spire.io/patients/${patientProfile.spireHealthPortalId}/charts`}
                      >
                        <span
                          className="ph-status-group__hp-ring"
                          aria-hidden="true"
                        >
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#1F8577"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M4 14c3-7 13-7 16 0"></path>
                          </svg>
                        </span>
                        HP
                      </a>
                      <span
                        className="ph-status-group__segment ph-status-group__segment--state"
                        style={{
                          background: stateMeta.background,
                          borderRight: `1px solid ${stateMeta.dividerColor}`,
                        }}
                      >
                        {stateMeta.glyph === "dot" && (
                          <span
                            className="ph-status-group__dot"
                            aria-hidden="true"
                            style={{ background: stateMeta.dotColor }}
                          />
                        )}
                        {(stateMeta.glyph === "speech" ||
                          stateMeta.glyph === "crossed") && (
                          <StateGlyphIcon
                            glyph={stateMeta.glyph}
                            stroke={stateMeta.ink}
                          />
                        )}
                        <span
                          className="ph-status-group__eyebrow"
                          style={{ color: stateMeta.ink }}
                        >
                          Monitoring Status
                        </span>
                        <span
                          className="ph-status-group__value"
                          style={{ color: stateMeta.ink }}
                        >
                          {stateMeta.label}
                        </span>
                      </span>
                      {activeTagCount > 0 && (
                        <a
                          className="ph-status-group__segment ph-status-group__segment--tags"
                          href={`https://hpd.spire.io/patients/${patientProfile.spireHealthPortalId}/devices`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="#2E4052"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <rect
                              x="7"
                              y="2.5"
                              width="10"
                              height="19"
                              rx="3"
                            ></rect>
                            <path d="M9.3 12h1.6l1 2.2 1.6-4.4 1 2.2h1.2"></path>
                          </svg>
                          <span className="ph-status-group__count">
                            {activeTagCount}
                          </span>
                          <span className="ph-status-group__unit">
                            {activeTagCount === 1 ? "tag" : "tags"}
                          </span>
                        </a>
                      )}
                    </>
                  )}
                  <button
                    type="button"
                    className="ph-status-group__segment ph-status-group__segment--rpm"
                    onClick={handleRpmOnlyToggleClick}
                    aria-label={
                      rpmOnlyFlag
                        ? "Remove RPM Only status from patient"
                        : "Mark patient as RPM Only"
                    }
                  >
                    <span
                      className={
                        rpmOnlyFlag
                          ? "ph-status-group__dot ph-status-group__dot--mint"
                          : "ph-status-group__dot"
                      }
                      aria-hidden="true"
                    />
                    <span className="ph-status-group__eyebrow">RPM Only</span>
                    <span
                      className={
                        rpmOnlyFlag
                          ? "ph-status-group__value ph-status-group__value--mint"
                          : "ph-status-group__value"
                      }
                    >
                      {rpmOnlyFlag ? "Yes" : "No"}
                    </span>
                  </button>
                </span>

                <span className="ph-strip__spacer" />

                <span className="ph-facts">
                  <span className="ph-facts__item">
                    <span className="ph-facts__label">Coach</span>
                    <span className="ph-facts__value">
                      {internalCareTeam?.primaryCoach?.name ?? "—"}
                    </span>
                  </span>
                  <span className="ph-facts__divider" />
                  <span className="ph-facts__item">
                    <span className="ph-facts__label">Last interactive</span>
                    <span className="ph-facts__value">
                      {formatDate(patientProfile?.lastInteractive) || "—"}
                    </span>
                  </span>
                  <span className="ph-facts__divider" />
                  <span className="ph-facts__item">
                    <span className="ph-facts__label">Language</span>
                    <span className="ph-facts__value">
                      {patientProfile?.preferredLanguageInfo?.type ?? "—"}
                    </span>
                  </span>
                </span>
              </div>
            </div>
          );
        })()}

      <Modal
        size={"xl"}
        show={modalShow}
        onHide={() => setModalShow(false)}
        aria-labelledby="containedCarePlan"
        centered
      >
        <>
          <Modal.Header className="px-8" closeButton>
            <Modal.Title id="containedCarePlan">
              <div> View Care Plan </div>
              <div className="fs-12 fw-normal">{`Reviewed On ${formatDateTimeToLocal(carePlan?.reviewedOn?.toLocaleString() || "")}`}</div>
            </Modal.Title>
          </Modal.Header>

          <ViewCarePlan
            onHide={() => setModalShow(false)}
            selectedCarePlan={carePlan}
          />
        </>
      </Modal>
      <Modal
        show={welcomeMailResendModal}
        onHide={() => setWelcomeMailResendModal(false)}
        aria-labelledby="containedCarePlan"
        centered
      >
        <>
          <Modal.Header className="px-8 border-0" closeButton>
            <div className="center-header">
              <span className="material-symbols-outlined fs-40 text-yellow">
                warning
              </span>
            </div>
          </Modal.Header>
          <div className="d-flex justify-content-center mb-2">
            Confirm<strong>&nbsp;send welcome email&nbsp;</strong>to patient?
          </div>
          <div className="d-flex justify-content-evenly  m-2">
            <button
              className="btn btn-danger rounded-pill px-5 fs-13"
              onClick={() => setWelcomeMailResendModal(false)}
            >
              No
            </button>
            <button
              className="btn btn-secondary rounded-pill px-5 fs-13"
              onClick={resendWelcomeMail}
            >
              Yes
            </button>
          </div>
        </>
      </Modal>
      <ConfirmationBox
        showConfirmBox={rpmOnlyConfirmShow}
        closeConfirmBox={() => setRpmOnlyConfirmShow(false)}
        performAction={handleConfirmRpmOnlyToggle}
        title="Change RPM Only status"
        message={
          rpmOnlyFlag
            ? "Are you sure you want to remove RPM Only status from this patient?"
            : "Are you sure you want to mark this patient as RPM Only?"
        }
      />
      <OffCanvas
        title={"Add Alert"}
        offCanvasShow={offCanvasShow}
        setOffCanvasShow={setOffCanvasShow}
      >
        <AddEditAlert
          patientId={patientProfile.patientId}
          setOffCanvasShow={setOffCanvasShow}
          handleShowToaster={handleShowToaster}
        />
      </OffCanvas>
    </>
  );
};
